import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search, Check } from 'lucide-react';

interface Option {
  id: string;
  name: string;
  category?: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  groupByCategory?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Select options',
  label,
  required = false,
  disabled = false,
  groupByCategory = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (optionName: string) => {
    if (selected.includes(optionName)) {
      onChange(selected.filter(s => s !== optionName));
    } else {
      onChange([...selected, optionName]);
    }
  };

  const removeOption = (optionName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(s => s !== optionName));
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedOptions = groupByCategory
    ? filteredOptions.reduce((acc, option) => {
        const category = option.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(option);
        return acc;
      }, {} as Record<string, Option[]>)
    : { All: filteredOptions };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-2.5 border rounded-lg text-left flex items-center justify-between transition-colors ${
            disabled
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200'
              : 'bg-white border-slate-300 hover:border-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          }`}
        >
          <span className={selected.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
            {selected.length === 0
              ? placeholder
              : `${selected.length} selected`}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
            <div className="p-2 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search trades..."
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {Object.keys(groupedOptions).length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  No trades found
                </div>
              ) : (
                Object.entries(groupedOptions).map(([category, categoryOptions]) => (
                  <div key={category}>
                    {groupByCategory && Object.keys(groupedOptions).length > 1 && (
                      <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        {category}
                      </div>
                    )}
                    {categoryOptions.map((option) => {
                      const isSelected = selected.includes(option.name);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => toggleOption(option.name)}
                          className={`w-full px-4 py-2.5 text-left flex items-center justify-between hover:bg-slate-50 transition-colors ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <span className={`text-sm ${isSelected ? 'font-medium text-blue-700' : 'text-slate-700'}`}>
                            {option.name}
                          </span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {selected.length > 0 && (
              <div className="p-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={clearAll}
                  className="w-full px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((item) => {
            const option = options.find(o => o.name === item);
            return (
              <span
                key={item}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
              >
                {option?.name || item}
                <button
                  type="button"
                  onClick={(e) => removeOption(item, e)}
                  className="hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
